import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const RETRY_COUNT = 2; // 2 retries = 3 total attempts
const BASE_DELAY_MS = 150;

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// Exported for unit testing.
export function extractChunkUrl(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/dynamically imported module:\s*(\S+)$/);
  return match ? match[1] : null;
}

async function tryCacheBustedImport<T>(chunkUrl: string): Promise<T> {
  const sep = chunkUrl.includes('?') ? '&' : '?';
  const bustUrl = `${chunkUrl}${sep}_t=${Date.now()}`;
  // Race against a timer so the retry loop does not stall in environments
  // where the dynamic import never settles (e.g. test runners with jsdom).
  //
  // The LOSER of the race must not leak its rejection: the import can reject
  // AFTER the timeout has already won (deterministic under jsdom / fake timers),
  // which surfaces as an unhandled "Failed to fetch dynamically imported module"
  // -- harmless at runtime but fatal to a CI test process. Attach a no-op catch
  // to the import so it is always handled, and clear the timer once the race
  // settles.
  const importPromise = import(/* @vite-ignore */ bustUrl) as Promise<T>;
  importPromise.catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('cache-bust import timeout')), 10_000);
  });
  try {
    return await Promise.race([importPromise, timeoutPromise]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

// Exported for unit testing (the retry loop is tested directly, without going
// through React.lazy -- whose held rejected thenable leaks an unhandled
// rejection under fake timers).
export async function retryImport<T>(
  factory: () => Promise<T>,
  retriesLeft: number,
  delayMs: number,
  lastError?: unknown,
): Promise<T> {
  // On retry: try a cache-busted URL to bypass edge-cached 503 responses.
  if (lastError !== undefined) {
    const chunkUrl = extractChunkUrl(lastError);
    if (chunkUrl) {
      try {
        return await tryCacheBustedImport<T>(chunkUrl);
      } catch {
        // Cache-busted import failed or timed out; fall through to factory().
      }
    }
  }

  try {
    return await factory();
  } catch (err) {
    // Offline, a retry cannot succeed (the chunk is not precached: docs/offline.md): fail at once so
    // ChunkErrorBoundary says "You're offline" instead of the person waiting out the retries.
    if (retriesLeft <= 0 || isOffline()) throw err;
    await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    return retryImport(factory, retriesLeft - 1, delayMs * 2, err);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, RETRY_COUNT, BASE_DELAY_MS));
}
